import { DataRouter, matchRoutes } from "react-router"
import { EventAttributes, PushEventOptions } from "@grafana/faro-core/dist/types/api/events/types"
import {
  createReactRouterV6DataOptions,
  faro,
  getWebInstrumentations,
  initializeFaro,
  MetaSession,
  ReactIntegration,
  withFaroRouterInstrumentation,
} from "@grafana/faro-react"
import { TracingInstrumentation } from "@grafana/faro-web-tracing"
import { SpanStatusCode } from "@opentelemetry/api"
import releaseInfo from "@/../release-info.json"
import { AppInsights } from "@/app-insights"
import { uuidv4 } from "@/lib/generate-uuidv4"
import { SofiaEnvironment } from "@/sofia-environment.ts"
import { faroEventStore } from "@/stores/faro-event-store"

const TAB_ID = uuidv4()

const IGNORED_ERROR_MESSAGE_PATTERNS = [/ResizeObserver loop/]

function initializeGrafanaFaroMonitoring(router: DataRouter) {
  console.log(`Initializing monitoring with Tab ID: ${TAB_ID}`)

  initializeFaro({
    url: SofiaEnvironment.FaroEndpoint,
    app: {
      name: `web-frontend`,
      namespace: "sofia",
      version: releaseInfo.imageTag,
      environment: SofiaEnvironment.CurrentEnvironment === "production" ? "prod" : SofiaEnvironment.CurrentEnvironment,
    },

    // We do not care about the following URLs, so we ignore them.
    ignoreUrls: [/.*.productfruits.com.*/, /.*.applicationinsights.azure.com.*/],

    ignoreErrors: IGNORED_ERROR_MESSAGE_PATTERNS,

    consoleInstrumentation: {
      // Even though console errors might be real errors, in most cases they either duplicate already recorded errors or are just noise.
      // This config keeps them from being recorded as errors, but still allows them to be recorded as logs.
      consoleErrorAsLog: true,
    },

    sessionTracking: {
      enabled: true,
      onSessionChange: (oldSession: MetaSession | null, newSession: MetaSession) => {
        console.log(`Session ${oldSession === null ? "created" : "changed"}`, { oldSession, newSession })
      },
    },
    instrumentations: [
      // Mandatory, omits default instrumentations otherwise.
      ...getWebInstrumentations(),

      // Tracing package to get end-to-end visibility for HTTP requests.
      new TracingInstrumentation({
        instrumentationOptions: {
          fetchInstrumentationOptions: {
            applyCustomAttributesOnSpan: (span, request, result) => {
              // Faro passes Request | RequestInit. Request.body is a ReadableStream (not a string),
              // so the JSON parsing below only works for RequestInit where .body is a string.
              if (request instanceof Request) return
              const requestInit = request as RequestInit
              if (requestInit.body && typeof requestInit.body === "string") {
                try {
                  const body = JSON.parse(requestInit.body)
                  if (body.operationName) {
                    span.updateName(body.operationName)
                  }
                } catch {
                  // If parsing fails, do nothing.
                }
              }
              // Record HTTP status text for non-OK responses
              if (!(result instanceof Error) && result instanceof Response && !result.ok) {
                span.setAttribute("http.status_text", result.statusText || "")
              }
              // Record fetch errors as span exceptions.
              if (result instanceof Error) {
                if (result.name === "AbortError") {
                  // We abort requests for many reasons (e.g. search input change, or terminating subscriptions)
                  // Probably better to have it UNSET rather than OK, but setStatus(UNSET) is ignored by faro.
                  span.setStatus({ code: SpanStatusCode.OK })
                } else {
                  span.recordException(result)
                  span.setStatus({ code: SpanStatusCode.ERROR, message: result.message })
                }
              }
            },
          },
        },
      }),
      // React integration for React applications.
      new ReactIntegration({
        router: createReactRouterV6DataOptions({
          matchRoutes,
        }),
      }),
    ],
  })

  withFaroRouterInstrumentation(router)
}

const FaroInsightsWrapper = {
  pushEvent: function (name: string, attributes?: EventAttributes, domain?: string, options?: PushEventOptions) {
    const enrichedAttributes: EventAttributes = {
      tabId: TAB_ID,
      ...attributes,
    }

    if (faro && faro.api && !faro.config.paused) {
      faro.api.pushEvent(name, enrichedAttributes, domain, options)
      faroEventStore.addEvent(name, enrichedAttributes, domain, options)
    }
    AppInsights.trackEvent({ name: name, properties: enrichedAttributes })
  },
}

export { initializeGrafanaFaroMonitoring, FaroInsightsWrapper }
